import React from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Message } from '../types';

interface MessageListProps {
  messages: Message[];
  isStreaming: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  showSuggestedPrompts?: boolean;
  onSuggestedPrompt?: (prompt: string) => void;
}

interface MessageBubbleProps {
  message: Message;
  isStreaming: boolean;
  isLastMessage: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isStreaming, isLastMessage }) => {
  const renderMessageContent = (text: string) => {
    if (message.sender === 'user') {
      return <div className="message-content">{text}</div>;
    }
    
    // For assistant messages, render as markdown
    const renderedMarkdown = DOMPurify.sanitize(marked.parse(text));
    
    return (
      <div 
        className="message-content"
        dangerouslySetInnerHTML={{ __html: renderedMarkdown }}
      />
    );
  };

  if (message.sender === 'user') {
    return (
      <div className="flex justify-end mb-4">
        <div className="bg-primary-500 text-white rounded-full px-4 py-2 max-w-xs lg:max-w-md">
          {renderMessageContent(message.text)}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start mb-4">
      <div className="text-gray-900 dark:text-gray-100 px-4 py-2 max-w-full">
        {message.text === '' && isStreaming && isLastMessage ? (
          <div className="flex items-center gap-2 py-2">
            <span className="pulsing-loader"></span>
          </div>
        ) : (
          <div className="relative">
            {renderMessageContent(message.text)}
            {isStreaming && isLastMessage && message.text !== '' && (
              <span className="streaming-cursor"></span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SuggestedPrompts: React.FC<{ onPromptClick: (prompt: string) => void }> = ({ onPromptClick }) => {
  const prompts = [
    "How do I request time off?",
    "How do I log my time?",
    "How do I access my benefits?",
    "How do I change my contact information?"
  ];

  return (
    <div className="mt-8 max-w-2xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {prompts.map((prompt, index) => (
          <button
            key={index}
            onClick={() => onPromptClick(prompt)}
            className="text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 
                       hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                       text-sm text-gray-700 dark:text-gray-300"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
};

const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  isStreaming, 
  messagesEndRef, 
  messagesContainerRef,
  onScroll,
  showSuggestedPrompts = false,
  onSuggestedPrompt
}) => {
  return (
    <div 
      ref={messagesContainerRef}
      className="h-full overflow-y-auto p-4 pb-8"
      onScroll={onScroll}
      style={{ scrollBehavior: 'smooth' }}
    >
      <div className="max-w-4xl mx-auto space-y-4">
        {messages.map((message, index) => (
          <MessageBubble 
            key={message.id}
            message={message}
            isStreaming={isStreaming}
            isLastMessage={index === messages.length - 1}
          />
        ))}
        
        {showSuggestedPrompts && onSuggestedPrompt && (
          <SuggestedPrompts onPromptClick={onSuggestedPrompt} />
        )}
        
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;